<template>
  <div class="bg-cercles">
    <div class="bb-login">
      <div class="bb-container">
        <div class="bb-login__container">
          <div class="login-box">

            <div v-if="loading" class="page-invite__loading">
              <LoadingButton />
            </div>

            <div v-else-if="notFound" class="page-invite__message">
              <p>{{ $t("invite.notFound") }}</p>
            </div>

            <template v-else-if="invitation">
              <h1 class="login-box__title">{{ $t("invite.title") }}</h1>
              <p class="page-invite__subtitle">{{ inviteSubtitle }}</p>
              <p v-if="invitation.listTitle && invitation.inviterName" class="page-invite__inviter">
                {{ $t("invite.invitedBy", { name: invitation.inviterName }) }}
              </p>

              <div v-if="accepted" class="page-invite__success">
                <div class="page-invite__success-icon">✓</div>
                <p class="page-invite__success-title">{{ acceptedTitle }}</p>
                <p class="page-invite__success-hint">{{ $t("invite.acceptedRedirect") }}</p>
              </div>

              <div v-else-if="acceptError" class="page-invite__error">
                <p>{{ acceptError }}</p>
              </div>

              <template v-else>
                <LoadingButton v-if="accepting" />
                <button v-else-if="isAuthenticated" class="button button--primary button--block button--lg" @click="accept">
                  {{ $t("invite.acceptButton") }}
                </button>
                <template v-else>
                  <div v-if="!showSignupForm" class="page-invite__auth-actions">
                    <p class="page-invite__helper">{{ $t("invite.helperText") }}</p>
                    <button class="button button--primary button--block button--lg" @click="loginToAccept">
                      {{ $t("invite.acceptButton") }}
                    </button>
                    <button class="button button--primary button--block button--lg" @click="openSignupForm">
                      {{ $t("invite.createAccount") }}
                    </button>
                    <LoginSocial
                      :show="true"
                      :showFacebook="false"
                      :showLabel="true"
                      :verticalAlign="true"
                      size="large"
                      origin="signup"
                      color="white"
                      :redirect="'/invite/' + token + '?accept=1'"
                    />
                  </div>

                  <div v-else class="page-invite__signup-form">
                    <div class="bb-form-item">
                      <label class="bb-form-label">{{ $t("signup.yourName") }}</label>
                      <FormInput
                        class="bb-form"
                        v-model="signupName"
                        name="name"
                        type="text"
                        :placeholder="$t('signup.yourName')"
                      />
                    </div>
                    <div class="bb-form-item">
                      <label class="bb-form-label">{{ $t("login.yourEmail") }}</label>
                      <FormInput
                        class="bb-form"
                        :text="email"
                        name="email"
                        type="email"
                        disabled
                      />
                    </div>
                    <div class="bb-form-item">
                      <label class="bb-form-label">{{ $t("login.password") }}</label>
                      <FormInput
                        class="bb-form"
                        v-model="password"
                        name="password"
                        type="password"
                        :placeholder="$t('login.password')"
                      />
                    </div>
                    <div v-if="signupError" class="bb-form-error">{{ signupError }}</div>
                    <div class="page-invite__form-actions">
                      <LoadingButton v-if="signingUp" />
                      <template v-else>
                        <button class="button button--primary button--block button--lg" @click="submitSignup">
                          {{ $t("invite.createAccount") }}
                        </button>
                        <button class="button button--secondary button--block button--lg" @click="showSignupForm = false">
                          {{ $t("generic.cancel") }}
                        </button>
                      </template>
                    </div>
                  </div>
                </template>
              </template>
            </template>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import LoadingButton from "../../components/LoadingButton.vue";
import LoginSocial from "../../components/ui/login/LoginSocial.vue";
import loginMixin from "../mixins/login.js";

export default {
  name: "InviteAcceptPage",
  components: { LoadingButton, LoginSocial },
  mixins: [loginMixin],
  data() {
    return {
      invitation: null,
      loading: true,
      notFound: false,
      accepting: false,
      accepted: false,
      acceptError: null,
      showSignupForm: false,
      signupName: "",
      email: "",
      password: "",
      signingUp: false,
      signupError: null,
    };
  },
  computed: {
    ...mapState(["isAuthenticated"]),
    token() {
      return this.$route.params.token;
    },
    acceptedTitle() {
      if (this.invitation && this.invitation.listTitle) {
        return this.$t("invite.acceptedWithList", { listTitle: this.invitation.listTitle });
      }
      return this.$t("invite.accepted");
    },
    inviteSubtitle() {
      if (!this.invitation) return "";
      if (this.invitation.listTitle) {
        return this.$t("invite.subtitleWithList", { listTitle: this.invitation.listTitle });
      }
      if (this.invitation.inviterName) {
        return this.$t("invite.subtitleWithName", { name: this.invitation.inviterName });
      }
      return this.$t("invite.subtitleFallback", { email: this.invitation.inviterEmail || "" });
    },
  },
  async mounted() {
    try {
      this.invitation = await this.$store.dispatch("fetchInvitationByToken", this.token);
      window.mixpanel.track("Col·laborador visualiza la invitació", {
        "Email": this.invitation?.email,
      });
    } catch {
      this.notFound = true;
    } finally {
      this.loading = false;
    }

    const { error } = this.$route.query;
    if (error === "emailMismatch") {
      this.acceptError = this.$t("invite.emailMismatch");
      return;
    }
    if (error === "notFound") {
      this.notFound = true;
      return;
    }

    if (this.isAuthenticated) {
      const shouldAccept = this.$route.query.accept || sessionStorage.getItem("pendingInviteToken") === this.token;
      if (shouldAccept) {
        sessionStorage.removeItem("pendingInviteToken");
        await this.accept();
      }
    }
  },
  methods: {
    loginToAccept() {
      sessionStorage.setItem("pendingInviteToken", this.token);
      this.$router.push({
        name: "login",
        query: { redirect: `/invite/${this.token}?accept=1` },
      });
    },
    openSignupForm() {
      this.email = (this.invitation && this.invitation.email) || "";
      this.showSignupForm = true;
    },
    async maybeSetDefaultList() {
      const listId = this.invitation && this.invitation.listId;
      if (!listId) return;
      try {
        await this.$store.dispatch("updateUserDefaultListId", listId);
      } catch {
        // non-blocking — redirect still happens
      }
    },
    async submitSignup() {
      if (!this.signupName.trim() || !this.password) {
        this.signupError = this.$t("generic.field_is_required");
        return;
      }
      this.signingUp = true;
      this.signupError = null;
      try {
        await this.$store.dispatch("signupViaInvitation", {
          token: this.token,
          name: this.signupName,
          email: this.email,
          password: this.password,
        });
        // Signup + invitation accepted in one step; now authenticate
        await this.login(null, false);
        await this.maybeSetDefaultList();
        this.accepted = true;
        setTimeout(() => this.$router.push({ name: "owner-list" }), 3000);
      } catch (e) {
        const status = e.response && e.response.status;
        if (status === 409) {
          // Email already registered — redirect to login flow
          this.loginToAccept();
        } else if (status === 403) {
          this.signupError = this.$t("invite.emailMismatch");
        } else {
          this.signupError = this.$t("generic.errorNoSave");
        }
      } finally {
        this.signingUp = false;
      }
    },
    async accept() {
      this.accepting = true;
      try {
        await this.$store.dispatch("acceptInvitation", this.token);
        await this.maybeSetDefaultList();
        this.accepted = true;
        setTimeout(() => this.$router.push({ name: "owner-list" }), 3000);
      } catch (e) {
        const status = e.response && e.response.status;
        if (status === 409) {
          // Invitation already accepted (e.g. via SSO signup) — treat as success
          await this.maybeSetDefaultList();
          this.accepted = true;
          setTimeout(() => this.$router.push({ name: "owner-list" }), 3000);
        }
        else if (status === 403) this.acceptError = this.$t("invite.emailMismatch");
        else this.acceptError = this.$t("generic.errorNoSave");
      } finally {
        this.accepting = false;
      }
    },
  },
};
</script>

<style scoped>
.page-invite__subtitle {
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.page-invite__inviter {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 1.5rem;
}
.page-invite__helper {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.25rem;
}
.page-invite__success {
  text-align: center;
  padding: 1.5rem 0;
}
.page-invite__success-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #22c55e;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 auto 1rem;
}
.page-invite__success-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #15803d;
  margin-bottom: 0.5rem;
}
.page-invite__success-hint {
  font-size: 0.875rem;
  color: #666;
}
.page-invite__error {
  color: red;
}
.page-invite__auth-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.page-invite__form-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}
</style>
